import { IonToggle } from '@ionic/react'
import { Snackbar } from '@mui/material'
import { $Enums, ContractType, ElectricityTransferPrices } from '@vae/server/dist/client'
import dayjs from 'dayjs'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../../analytics/utils/firebaseUtils'
import Button from '../../../components/actions/Button'
import DateInput from '../../../components/inputs/DateInput'
import RadioInput from '../../../components/inputs/RadioInput'
import { electricTaxPerKwh } from '../../../constants'
import { ObjectContext } from '../../../contexts/objectContext/ObjectContext'
import { trpc } from '../../../modules/trpc'
import { formatMoney } from '../../../utils/formatMoney'
import CompanySelect from '../components/CompanySelect'
import ContractTypeSelect from '../components/ContractTypeSelect'
import PriceInput from '../components/PriceInput'
import { useEditObjectMutation, useElectricityTransferPricesQuery } from '../hooks/trpcHooks'

/**
 * Component to display contract options for electricity transfer prices.
 * @returns {JSX.Element} The rendered component.
 */

export type ContractTypeMode = 'fixed' | 'nightAndDay'

const ContractOptions = () => {
	const object = useContext(ObjectContext)
	const { t, i18n } = useTranslation('modals_contractOptions')
	const { t: tc } = useTranslation('pages_onboarding')

	const trpcUtils = trpc.useContext()

	const [open, setOpen] = useState(false)
	const [message, setMessage] = useState('')

	const [contractType, setContractType] = useState<ContractType | null>(object.contractType ?? null)
	const [contractEndDate, setContractEndDate] = useState(
		object.contractEndDate ? dayjs(object.contractEndDate).format('YYYY-MM-DD') : null
	)
	const [contractEndDateType, setContractEndDateType] = useState(object.contractEndDateType ?? null)
	const [monthlyPrice, setMonthlyPrice] = useState(object.monthlyPrice?.toString() ?? '0')
	const [spotMarginal, setSpotMarginal] = useState(object.spotMarginal?.toString() ?? '0')
	const [fixedPrice, setFixedPrice] = useState(object.fixedPrice?.toString() ?? '0')
	const [dayPricePerKwh, setDayPricePerKwh] = useState(
		object.electricityTransferPriceContractDayPrice?.toString() ?? '0'
	)
	const [nightPricePerKwh, setNightPricePerKwh] = useState(
		object.electricityTransferPriceContractNightPrice?.toString() ?? '0'
	)
	const [transferMonthlyPrice, setTransferMonthlyPrice] = useState<string>(
		object?.electricityTransferMonthlyPrice?.toString() ?? '0'
	)

	const [selectedCompany, setSelectedCompany] = useState<ElectricityTransferPrices | undefined>(
		undefined
	)

	const [pricePerKwh, setPricePerKwh] = useState<string>(
		object?.electricityTransferPricePerKwh?.toString() ?? '0'
	)

	const [isElectricTaxIncluded, setIsElectricTaxIncluded] = useState(object.electricityTaxIncluded)

	const objectMutation = useEditObjectMutation(trpcUtils)
	const { data: electricityTransferPrices } = useElectricityTransferPricesQuery()

	/**
	 * Handles the change of the electricity tax display and updates the local state.
	 */

	const handleChangeElectricityTaxDisplay = () => {
		const newValue = !isElectricTaxIncluded
		setIsElectricTaxIncluded(newValue)
		logEvent(`toggle-electric-tax`, { value: newValue })

		objectMutation.mutate(
			{
				id: object.id,
				data: {
					electricityTaxIncluded: newValue,
				},
			},
			{
				onError: () => {
					setOpen(true)
					setMessage(t('saveError'))
					// Revert on error
					setIsElectricTaxIncluded(!newValue)
				},
				onSuccess: () => {
					setOpen(true)
					setMessage(t('saveSuccess'))
				},
			}
		)
	}

	/**
	 * Handles saving the electricity prices by mutating the object context.
	 */
	const saveElectricityPrices = () => {
		const data = {
			electricityTransferPriceId: selectedCompany?.id,
			electricityTransferMonthlyPrice: parseFloat(transferMonthlyPrice) || 0,
			electricityTransferPricePerKwh: parseFloat(pricePerKwh) || 0,
			monthlyPrice: parseFloat(monthlyPrice) || 0,
			spotMarginal: parseFloat(spotMarginal) || 0,
			contractType: contractType as 'fixed' | 'spot' | null | undefined,
			fixedPrice: parseFloat(fixedPrice) || 0,
			contractEndDate: contractEndDate ? dayjs(contractEndDate).format('YYYY-MM-DD') : null,
			contractEndDateType: contractEndDateType as 'indefinite' | 'fixedTerm' | null | undefined,
			electricityTransferPriceContractType: currentMode,
			electricityTransferPriceContractDayPrice: parseFloat(dayPricePerKwh) || 0,
			electricityTransferPriceContractNightPrice: parseFloat(nightPricePerKwh) || 0,
		}

		logEvent(`click-save-transfer-price`, { saved: data })
		objectMutation.mutate(
			{
				id: object.id,
				data: data,
			},
			{
				onError: () => {
					setOpen(true)
					setMessage(t('saveError'))
				},
				onSuccess: () => {
					setOpen(true)
					setMessage(t('saveSuccess'))
				},
			}
		)
	}

	const handleTransferPriceChange = (value: string) => {
		setTransferMonthlyPrice(value)
	}

	/**
	 * Handles the change of the selected company and updates the local state with the company's prices.
	 * @param companyName - The name of the selected company.
	 */
	const handleCompanyChange = (companyName: string) => {
		logEvent(`select-transfer-price-company`, { name: companyName })
		const selected = electricityTransferPrices?.find(price => price.companyName === companyName)
		setSelectedCompany(selected)
		if (selected) {
			setTransferMonthlyPrice(selected.monthlyPrice?.toString() ?? '0')
			setPricePerKwh(selected.pricePerKwh?.toString() ?? '0')
			setDayPricePerKwh(selected.pricePerKwh?.toString() ?? '0')
			setNightPricePerKwh(selected.pricePerKwh?.toString() ?? '0')
		}
	}

	useEffect(() => {
		if (object?.electricityTransferPriceId && electricityTransferPrices) {
			const selected = electricityTransferPrices.find(
				price => price.id === object.electricityTransferPriceId
			)
			setSelectedCompany(selected)
			setTransferMonthlyPrice(
				object.electricityTransferMonthlyPrice?.toString() ??
					selected?.monthlyPrice?.toString() ??
					'0'
			)
			setPricePerKwh(
				object.electricityTransferPricePerKwh?.toString() ??
					selected?.pricePerKwh?.toString() ??
					'0'
			)

			setDayPricePerKwh(
				object.electricityTransferPriceContractDayPrice?.toString() ??
					selected?.pricePerKwh?.toString() ??
					'0'
			)
			setNightPricePerKwh(
				object.electricityTransferPriceContractNightPrice?.toString() ??
					selected?.pricePerKwh?.toString() ??
					'0'
			)
		}
	}, [object?.electricityTransferPriceId, electricityTransferPrices])

	const isSpot = contractType === 'spot'
	const hasContractType = contractType

	const onChangeMargin = (value: string) => {
		setSpotMarginal(value)
	}

	const onChangeFixedPrice = (value: string) => {
		setFixedPrice(value)
	}

	const onChangeMonthlyPrice = (value: string) => {
		setMonthlyPrice(value)
	}

	const onChangeContractEndDate = (value: string | null) => {
		setContractEndDate(dayjs(value).format('YYYY-MM-DD') ?? 0)
	}

	const getContractTypeMode = (type: string | null): ContractTypeMode => {
		if (type === 'nightAndDay') return 'nightAndDay'
		if (type === 'fixed') return 'fixed'
		return 'fixed' // Default case for null or any other value
	}

	const [currentMode, setCurrentMode] = useState(
		getContractTypeMode(object.electricityTransferPriceContractType)
	)

	return (
		<div className='company-container'>
			<div className='input-container'>
				<span className='subtitle dark:text-text-grey-100 text-text-grey-200'>
					{t('siirtohinnat')}
				</span>

				<CompanySelect
					selectedCompany={selectedCompany}
					companyList={electricityTransferPrices}
					onCompanyChange={handleCompanyChange}
				/>

				<PriceInput
					title='electricityTransferMonthlyPrice'
					value={transferMonthlyPrice}
					onValueChange={handleTransferPriceChange}
					unit={t('unit1')}
				/>

				<div className='input-container'>
					<RadioInput
						isChecked={currentMode === 'fixed'}
						label={t('fixedContract')}
						onChange={() => setCurrentMode('fixed')}
					/>
					<RadioInput
						isChecked={currentMode === 'nightAndDay'}
						label={t('nightDayContract')}
						onChange={() => setCurrentMode('nightAndDay')}
					/>
				</div>

				<div className='electric-tax flex flex-row justify-between items-center'>
					<div className='texts text-text-dark dark:text-white'>
						<div className='text'> {t('electricTaxQuestion')}</div>
						<div className='small-text'>
							{t('electricTaxExplanation', { rate: formatMoney(electricTaxPerKwh, i18n.language) })}
						</div>
					</div>
					<IonToggle
						checked={isElectricTaxIncluded}
						onClick={handleChangeElectricityTaxDisplay}
						className='[--track-background:#d6d6d6] dark:[--track-background:#09292b] [--handle-background:#a0a0a0] dark:[--handle-background:#063a3e] [--track-background-checked:#a8da9a] dark:[--track-background-checked:#0a5a5f] [--handle-background-checked:#52ae32] dark:[--handle-background-checked:#3cb49f]'
					/>
				</div>
			</div>

			{currentMode === 'fixed' ? (
				<>
					<PriceInput
						title='electricityTransferPricePerKwh'
						value={pricePerKwh}
						onValueChange={setPricePerKwh}
						unit={t('unit2')}
					/>
				</>
			) : (
				<>
					<PriceInput
						title='nightTransferPrice'
						value={nightPricePerKwh}
						onValueChange={setNightPricePerKwh}
						unit={t('unit2')}
					/>

					<PriceInput
						title='dayTransferPrice'
						value={dayPricePerKwh}
						onValueChange={setDayPricePerKwh}
						unit={t('unit2')}
					/>
				</>
			)}
			{object.useDatahub && !object.synerallContractId && (
				<div className='input-container'>
					<span className='subtitle'>{tc('contractTypeSlide.sopimus')}</span>
					<ContractTypeSelect
						value={contractType ?? undefined}
						onChange={value => setContractType(value as ContractType | null)}
					/>
					{hasContractType && (
						<PriceInput
							title={tc('contractTypeSlide.perusmaksu')}
							unit={t('unit1')}
							onValueChange={onChangeMonthlyPrice}
							value={monthlyPrice}
						/>
					)}
					{hasContractType && (
						<PriceInput
							title={isSpot ? tc('contractTypeSlide.marginaali') : tc('contractTypeSlide.hinta')}
							unit={t('unit2')}
							onValueChange={isSpot ? onChangeMargin : onChangeFixedPrice}
							value={isSpot ? spotMarginal : fixedPrice}
						/>
					)}
					<RadioInput
						isChecked={contractEndDateType === $Enums.ContractEndDateType.indefinite}
						label='Toistaiseksi voimassaoleva'
						onChange={() => setContractEndDateType($Enums.ContractEndDateType.indefinite)}
					/>
					<RadioInput
						isChecked={contractEndDateType === $Enums.ContractEndDateType.fixedTerm}
						label='Määräaikainen'
						onChange={() => setContractEndDateType($Enums.ContractEndDateType.fixedTerm)}
					/>
					{contractEndDateType === $Enums.ContractEndDateType.fixedTerm && (
						<DateInput
							minDate={dayjs().format('YYYY-MM-DD')}
							label={tc('contractTypeSlide.endDate')}
							value={contractEndDate ?? ' '}
							onChange={onChangeContractEndDate}
						/>
					)}
				</div>
			)}

			<Button onClick={saveElectricityPrices}>{t('tallenna', { ns: 'common' })}</Button>

			<Snackbar
				autoHideDuration={4000}
				message={message}
				open={open}
				onClose={() => setOpen(false)}
			/>
		</div>
	)
}

export default ContractOptions
